/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Added CSV export functionality.
 *                                   - Ensured `totalItems` prop is passed to Pagination.
 *                                   - Integrated Spinner for loading states.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically hide columns
 *                                   and customize column headers based on whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This component displays a list of surcharge definitions.
 *                      It does not handle sensitive data directly.
 * INTENT: Render a paginated table of Surcharge records with actions for editing,
 *         deleting, and exporting data, providing a clear overview and management interface,
 *         now with whitelabeling capabilities.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure pagination logic is correctly integrated
 *                        and that actions are clearly visible and accessible.
 * LLM_CONFIDENCE: 100% - Standard table component with pagination and action buttons.
 */
import React, { useState } from 'react';
import { Surcharge } from '../types/costwand.d';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table.tsx';
import { Button } from './ui/button.tsx';
import Pagination from './ui/Pagination.tsx';
import { Spinner } from './ui/spinner.tsx';
import { apiClient } from '../api/apiClient.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

interface SurchargeTableProps {
  surcharges: Surcharge[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onEdit: (surcharge: Surcharge) => void;
  onDelete: (id: number) => void;
  loading: boolean;
  error: string | null;
  baseEndpoint: string;
  entityId: string; // NEW: Add entityId prop
}

const SurchargeTable: React.FC<SurchargeTableProps> = ({
  surcharges,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  onEdit,
  onDelete,
  loading,
  error,
  baseEndpoint,
  entityId, // NEW: Destructure entityId
}) => {
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context
  const [exportLoading, setExportLoading] = useState(false);

  const getFieldConfig = (fieldId: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId];

  const isFieldVisible = (fieldId: string) =>
    getFieldConfig(fieldId)?.visible !== false; // Default to visible

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    getFieldConfig(fieldId)?.title || defaultTitle;

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: unknown) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) return <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading surcharges...</p>;
  if (surcharges.length === 0 && !loading) return <p className="text-center text-muted-foreground">No surcharges found.</p>;

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4">
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            {isFieldVisible('description') && <TableHead>{getFieldTitle('description', 'Description')}</TableHead>}
            {isFieldVisible('code') && <TableHead>{getFieldTitle('code', 'Code')}</TableHead>}
            {isFieldVisible('percentage') && <TableHead>{getFieldTitle('percentage', 'Percentage')}</TableHead>}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {surcharges.map((surcharge) => (
            <TableRow key={surcharge.id}>
              {isFieldVisible('description') && <TableCell className="font-medium">{surcharge.description}</TableCell>}
              {isFieldVisible('code') && <TableCell>{surcharge.code}</TableCell>}
              {isFieldVisible('percentage') && <TableCell>{surcharge.percentage?.toFixed(2)}%</TableCell>}
              <TableCell className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => onEdit(surcharge)}>Edit</Button>
                <Button variant="destructive" size="sm" onClick={() => onDelete(surcharge.id!)}>Delete</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        disabled={loading || exportLoading}
      />
    </div>
  );
};

export default SurchargeTable;